import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { throttle } from 'lodash-es';
import { type BundleRating } from '@hungryroot/rest';
import { useIdentify } from '@hungryroot/rest-hooks';
import { useDeleteCustomerBundleRating } from '../../../hooks/useDeleteCustomerBundleRating';
import { usePatchBundleRatingsMutation } from '../../../hooks/usePatchBundleRatingsMutation';
import { usePostBundleRatingsMutation } from '../../../hooks/usePostBundleRatingsMutation';
import {
  useSelfBundleRating,
  invalidateQuery as invalidateSelfBundleRatingQuery,
  getQueryKey as getSelfBundleRatingQueryKey,
} from '../../../hooks/useSelfBundleRating';
import { invalidateQuery as invalidateSelfFoodFeedbacksQuery } from '../../../hooks/useSelfFoodFeedbacks';
import { useOptimisticUpdateStatus } from '../useOptimisticUpdateStatus';
import { type RatablePairingItem } from '../useRatableItems';

type UseThumbsBundleRatingProps = {
  item: RatablePairingItem & { bundleId: number };
  onError?: () => void;
  enableInitialFetch?: boolean;
  initialRating?: boolean | null;
};

type MutationContext = {
  previousRating?: BundleRating;
};

export function useThumbsBundleRating(props: UseThumbsBundleRatingProps) {
  const { item, onError, enableInitialFetch = false, initialRating } = props;

  const {
    optimisticUpdateStatus,
    hasRated,
    postCustomerBundleRatingsMutate,
    deleteCustomerBundleRatingsMutate,
    patchCustomerBundleRatingsMutate,
  } = useRatingMutation({
    item,
    onError,
    enableInitialFetch,
  });

  const { customerData } = useIdentify();
  const [hasSetInitialRating, setHasSetInitialRating] = useState(false);

  const {
    data: selfBundleRatingData,
    isPending: selfBundleRatingIsPending,
    error: selfBundleRatingError,
  } = useSelfBundleRating({
    params: {
      bundle_id: item.bundleId,
    },
    options: {
      enabled: hasRated,
    },
  });

  // Throttled API calls to prevent sending too many requests if the user
  // decides to rage click. This function handles which API call to make
  // based on the current state of the rating.
  const memoizedOnRatingApiCall = useMemo(
    () =>
      throttle((newValue: boolean | null) => {
        if (!customerData?.id) {
          return;
        }

        // There are no previous rating that we know of. Create a new one only
        // if the rating is a thumbs up or thumbs down (i.e., not `null`).
        if (!selfBundleRatingData && newValue !== null) {
          postCustomerBundleRatingsMutate({
            customer_id: customerData?.id,
            bundle: item.bundleId,
            liked: newValue,
            pairing: item.pairingId,
          });

          return;
        }

        // There's a previous rating and the user opted to remove it.
        if (selfBundleRatingData && newValue === null) {
          deleteCustomerBundleRatingsMutate({
            customer_id: customerData.id,
            id: item.bundleId,
          });

          return;
        }

        // There's a previous rating and the user opted to change it (not deactivate).
        if (selfBundleRatingData && newValue !== null) {
          patchCustomerBundleRatingsMutate({
            customer_id: customerData.id,
            bundle_id: item.bundleId,
            liked: newValue,
            pairing: item.pairingId,
          });

          return;
        }
      }, 1000),
    [
      item.bundleId,
      selfBundleRatingData,
      customerData?.id,
      postCustomerBundleRatingsMutate,
      deleteCustomerBundleRatingsMutate,
      patchCustomerBundleRatingsMutate,
      item.pairingId,
    ]
  );

  // If given an intitial rating, handle the API call to set it if it does not match
  // what the server gives us.
  useEffect(() => {
    if (
      selfBundleRatingIsPending ||
      initialRating === undefined ||
      hasSetInitialRating
    ) {
      return;
    }

    if (
      (selfBundleRatingError && initialRating !== null) ||
      (selfBundleRatingData && selfBundleRatingData.liked !== initialRating)
    ) {
      memoizedOnRatingApiCall(initialRating);
      setHasSetInitialRating(true);
    }
  }, [
    selfBundleRatingData,
    initialRating,
    hasSetInitialRating,
    memoizedOnRatingApiCall,
    selfBundleRatingData?.liked,
    selfBundleRatingIsPending,
    selfBundleRatingError,
  ]);

  let rating;

  if (initialRating && !hasSetInitialRating) {
    rating = initialRating;
  } else if (
    initialRating &&
    hasSetInitialRating &&
    selfBundleRatingIsPending
  ) {
    rating = initialRating;
  } else {
    rating = selfBundleRatingData?.liked ?? null;
  }

  return {
    rating,
    onRating: memoizedOnRatingApiCall,
    optimisticUpdateStatus,
  };
}

type UseRatingMutationProps = {
  item: RatablePairingItem & { bundleId: number };
  onError?: () => void;
  enableInitialFetch: boolean;
};

function useRatingMutation(props: UseRatingMutationProps) {
  const { item, onError, enableInitialFetch = false } = props;

  const queryKey = getSelfBundleRatingQueryKey({ bundle_id: item.bundleId });

  // Keeps track of whether or not the user has rated this pairing at least
  // once. This is used to determine whether or not we should initally fetch
  // the latest rating from the server.
  const [hasRated, setHasRated] = useState(enableInitialFetch);

  const { optimisticUpdateStatus, setOptimisticUpdateStatus } =
    useOptimisticUpdateStatus();

  const queryClient = useQueryClient();

  const onPostOrPatchMutate = async (payload: { liked: boolean | null }) => {
    setOptimisticUpdateStatus('pending');

    await queryClient.cancelQueries({ queryKey });

    const previousRating: BundleRating | undefined =
      queryClient.getQueryData(queryKey);

    // Create an object that represents the bundle rating details as
    // if the API call was successful.
    const optimisticUpdate: BundleRating = {
      ...previousRating,
      bundle: item.bundleId,
      liked: payload.liked,
    };

    queryClient.setQueryData(queryKey, optimisticUpdate);

    // Provide context for the mutation so we can revert the optimistic
    // update if anything goes wrong.
    return { previousRating };
  };

  // `onError` handler when making the `POST` or `PATCH` call.
  const onPostOrPatchError = () => {
    onError?.();

    // Remove the query data from the cache. If something went wrong here,
    // the data in the cache is likely not accurate and may cause us to attempt
    // to make the wrong type of request. Best to start off fresh.
    queryClient.removeQueries({ queryKey });
  };

  // `onSettled` handler when making the `POST`, `PATCH`, or `DELETE` call.
  const onSettled = () => {
    setOptimisticUpdateStatus('settled');
    setHasRated(true);

    // Invalidate the query so that the latest rating is fetched from the
    // server. The timeout is to prevent the GET from happening too soon
    // after to prevent stale data.
    setTimeout(() => {
      invalidateSelfBundleRatingQuery({
        bundle_id: item.bundleId,
        queryClient,
      });

      invalidateSelfFoodFeedbacksQuery({
        params: {
          order: item.orderNumber,
          bundle:
            'bundleId' in item && item.bundleId ? item.bundleId : undefined,
          pairing:
            'pairingId' in item && item.pairingId ? item.pairingId : undefined,
        },
        queryClient,
      });
    }, 500);
  };

  const { mutate: postCustomerBundleRatingsMutate } =
    usePostBundleRatingsMutation<MutationContext>({
      onMutate: onPostOrPatchMutate,
      onError: onPostOrPatchError,
      onSettled,
    });

  const { mutate: patchCustomerBundleRatingsMutate } =
    usePatchBundleRatingsMutation<MutationContext>({
      onMutate: onPostOrPatchMutate,
      onError: onPostOrPatchError,
      onSettled,
    });

  const { mutate: deleteCustomerBundleRatingsMutate } =
    useDeleteCustomerBundleRating({
      onMutate: async () => {
        setOptimisticUpdateStatus('pending');

        await queryClient.cancelQueries({ queryKey });

        const previousRating: BundleRating | undefined =
          queryClient.getQueryData(queryKey);

        queryClient.setQueryData(queryKey, null);

        return { previousRating };
      },

      onError: (_error, _variables, context) => {
        onError?.();

        if (context) {
          queryClient.setQueryData(queryKey, context.previousRating);
        }
      },

      onSettled,
    });

  return {
    optimisticUpdateStatus,
    hasRated,
    postCustomerBundleRatingsMutate,
    deleteCustomerBundleRatingsMutate,
    patchCustomerBundleRatingsMutate,
  };
}
